/**
 * @prettier
 */

// package dependencies
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import Clipboard from 'clipboard';
import Dropdown, { DropdownTrigger, DropdownContent } from 'react-simple-dropdown';
import { IconButton, ButtonV2, Banner, Link } from '@zola/zola-ui';
import MediaQuery from 'react-responsive';
import Joyride from 'react-joyride';

import { NAV_DATA } from 'constants/navConstants';
import SecondaryNavV2 from 'components/common/header/SecondaryNavV2';

// Utils
import { trackGLOnboardStepCompleted, trackGLOnboardCompleted } from '../../../util/trackingHelper';

// Actions
import { showModal, hideModal } from '../../../actions/ModalActions';
import {
  getGuestGroup,
  fetchGuestList,
  toggleInvited,
  toggleAllGuestsFacetValue,
  toggleSortByNameAsc,
} from '../../../actions/GuestActions';
import { updateFeatureFlag } from '../../../actions/WeddingActions';
import { resetMapping } from '../../../actions/GuestImportActions';

// Components
import Loader from '../../common/ui/Loader';
import MatchGuestList from './upload/MatchGuestList';
import StickyHeaderTable from '../../common/ui/StickyHeaderTable';
import Facets from './Facets';
import NoGuestsOnboardForm from './noGuestsOnboardForm/NoGuestsOnboardForm';
import NoGuestsOnboardFormV2 from './noGuestsOnboardFormV2/NoGuestsOnboardFormV2';
import NoGuestValueProps from './noGuestValueProps/NoGuestValueProps';
import GuestsTable from './directoryEntities/GuestsTable';
import GuestsHeader from './directoryEntities/GuestsHeader';
import DirectoryNavV2 from './DirectoryNavV2';
import TourTooltip from '../../common/ui/Tour/TourTooltip/TourTooltip';
import { Step1, Step2, Step3, Step4, Step5 } from '../../../pages/TourSteps';

import { getIncompleteAddressGuestGroupIds } from './util';
import featureFlags from '../../../util/featureFlags';

const isFlagGuestListMobileFix = featureFlags.get('flagGuestListMobileFix');
const isGuestListTrackingEvents = featureFlags.get('isGuestListTrackingEvents');
const isGuestListActivationV2 = featureFlags.get('guestListActivationV2');
const useHotelPlanner = featureFlags.get('hotelPlannerBanner');

const steps = [
  {
    target: '#GL-step1',
    content: <Step1 />,
    disableBeacon: true,
    stepName: 'spreadsheet',
  },
  {
    target: '#GL-step2',
    content: <Step2 />,
    disableBeacon: true,
    stepName: 'manual',
  },
  {
    target: '.first-set .global-stats .global-stat:nth-child(3)',
    content: <Step3 />,
    disableBeacon: true,
    stepName: 'bulk_missing_address',
  },
  {
    target: '.incomplete-mailing-address',
    content: <Step4 />,
    disableBeacon: true,
    placement: 'left',
    stepName: 'single_missing_address',
  },
  {
    target: '#GL-step5',
    content: <Step5 />,
    disableBeacon: true,
    placement: 'left',
    stepName: 'export',
  },
];
const stepsMobile = [
  {
    target: '#GL-step2',
    content: <Step2 />,
    disableBeacon: true,
    placement: 'center',
    stepName: 'manual',
  },
  {
    target: '.incomplete-mailing-address',
    content: <Step4 />,
    disableBeacon: true,
    placement: 'center',
    stepName: 'single_missing_address',
  },
];

class GuestDirectory extends Component {
  constructor(props) {
    super(props);

    this.checkRow = this.checkRow.bind(this);
    this.checkAllRows = this.checkAllRows.bind(this);
    this.unCheckAllRows = this.unCheckAllRows.bind(this);
    this.resetFacets = this.resetFacets.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.toggleGuestBreakdown = this.toggleGuestBreakdown.bind(this);
    this.showAllGuests = this.showAllGuests.bind(this);
    this.updateFlowRunId = this.updateFlowRunId.bind(this);

    this.state = {
      checkedRows: [],
      checkedAllRows: false,
      clipboard: undefined,
      joyrideRun: false,
      flowRunId: undefined,
    };
  }

  componentDidMount() {
    const { getGuestList, getEvents } = this.props;
    getGuestList();
    getEvents();
    setTimeout(() => this.setState({ joyrideRun: true }), 200); // Seems to initialize the tour quicker.
    this.initializeClipboard();
    this.updateFlowRunId();
  }

  componentWillReceiveProps(nextProps) {
    const { filteredIds } = nextProps;
    const { checkedRows } = this.state;

    const nextCheckedRows = _.intersection(checkedRows, filteredIds);
    const nextCheckedAllRows =
      nextCheckedRows.length !== 0 && nextCheckedRows.length === filteredIds.length;

    this.setState({
      checkedRows: nextCheckedRows,
      checkedAllRows: nextCheckedAllRows,
    });
  }

  componentDidUpdate(prevProps) {
    const { isMapping, getGuestList } = this.props;
    if (!isMapping && prevProps.isMapping) getGuestList();
  }

  componentWillUnmount() {
    const { onUnload } = this.props;
    onUnload();
    this.destroyClipboard();
    this.resetAll();
  }

  handleJoyrideCallback = data => {
    const { onDisableTour, weddingAcctUUID } = this.props;
    const { index, type, action } = data;
    const isStepAfter = type === 'step:after';
    const isStepSkip =
      (isStepAfter && action === 'skip') || (isStepAfter && index === steps.length - 1);
    const stepUserResponse = isStepSkip ? 'End Tour' : 'Next';

    // if(data.action === 'skip' || data.action === 'reset') {
    if (data.type === 'tour:end') {
      // disable
      onDisableTour(weddingAcctUUID);
    }
    // fire some tracking
    if (isGuestListTrackingEvents) {
      if (isStepAfter) {
        trackGLOnboardStepCompleted({
          step_key: index + 1,
          step_name: steps[index].stepName,
          questionnaire_version: 1,
          step_user_response: stepUserResponse,
        });

        if (isStepSkip) {
          trackGLOnboardCompleted();
        }
      }
    }
  };

  checkRow(id, isChecked) {
    const { filteredIds } = this.props;
    const { checkedRows } = this.state;

    if (isChecked) {
      checkedRows.push(id);
    } else {
      _.pull(checkedRows, id);
    }

    this.setState({
      checkedRows,
      checkedAllRows: filteredIds.length === checkedRows.length,
    });
  }

  checkAllRows() {
    const { filteredIds } = this.props;
    const checkedAllState = !this.state.checkedAllRows;

    this.setState({
      checkedRows: checkedAllState ? filteredIds.slice() : [],
      checkedAllRows: checkedAllState,
    });
  }

  unCheckAllRows() {
    this.setState({
      checkedRows: [],
      checkedAllRows: false,
    });
  }

  initializeClipboard() {
    const clipboard = new Clipboard('.collect-address-copy');

    clipboard.on('success', e => {
      const target = e.trigger;
      target.setAttribute('data-tooltip', 'Copied!');
      setTimeout(() => {
        target.removeAttribute('data-tooltip');
      }, 1000);
    });

    clipboard.on('error', () => {
      let clipboardErrorMessage = '';
      if (/iPhone|iPad/i.test(navigator.userAgent)) {
        clipboardErrorMessage = 'Select and copy.';
      } else if (/Mac/i.test(navigator.userAgent)) {
        clipboardErrorMessage = 'Press ⌘-C to copy.';
      } else {
        clipboardErrorMessage = 'Press Ctrl-C to copy.';
      }
      this.setState({ clipboardErrorMessage });
      setTimeout(() => this.setState({ clipboardErrorMessage: undefined }), 5000);
    });

    this.setState({ clipboard });
  }

  destroyClipboard() {
    const { clipboard } = this.state;
    if (clipboard) clipboard.destroy();
    this.setState({
      clipboard: undefined,
    });
  }

  resetFacets() {
    const { onResetFacets, getGuestList } = this.props;
    onResetFacets();
    getGuestList();
  }

  resetAll() {
    const { onResetFacets, onResetSearchValue, onSelectGuestGroup } = this.props; // eslint-disable-line
    onResetSearchValue();
    onSelectGuestGroup(null);
  }

  toggleGuestBreakdown() {
    this.setState(state => ({
      showGuestBreakdown: !state.showGuestBreakdown,
    }));
  }

  showAllGuests() {
    const { onResetFacets, onResetSearchValue, getGuestList } = this.props;
    onResetSearchValue();
    onResetFacets();
    getGuestList();
  }

  updateFlowRunId() {
    const { weddingAcctUUID } = this.props;
    this.setState({
      flowRunId: weddingAcctUUID + Date.now().toString(),
    });
  }

  render() {
    const {
      isMapping,
      onRequestMissingInfo,
      onGuestMessage,
      onToggleFacet,
      wedding,
      onAddGuestGroup,
      onUploadSpreadsheet,
      guestGroups,
      onToggleInvited,
      onEditGuestGroup,
      globalStats,
      numGuests,
      numSelectedFacets,
      onDeleteGuestGroups,
      sortByNameAsc,
      onToggleSort,
      selectedFacetValues,
      editGuestModalOpen,
      initialized,
      onSearchFilterGuestList,
      searchValue,
      filteredIds,
      onConfirmAction,
      onSelectGuestGroup,
      selectedGuestGroupId,
      showTour, // eslint-disable-line
    } = this.props;

    const { checkedRows, checkedAllRows } = this.state;

    if (!initialized) {
      return (
        <div className="guestlist-loader">
          <Loader delay />
        </div>
      );
    }

    if (isMapping)
      return (
        <MatchGuestList
          onTryAgain={() => onUploadSpreadsheet(this.state.flowRunId, this.updateFlowRunId)}
          flowRunId={this.state.flowRunId}
          updateFlowRunId={this.updateFlowRunId}
        />
      );

    let definitelyInvitedCount;
    if (_.find(globalStats, { key: 'invited_guests' })) {
      definitelyInvitedCount = _.find(globalStats, { key: 'invited_guests' }).value;
    }
    const formattedGlobalStats = [];
    const formattedGuestAgeStats = [];
    let numGuestsAttending = 0;

    globalStats.forEach(globalStat => {
      if (globalStat.key === 'addresses_missing' && globalStat.value > 0) {
        formattedGlobalStats.push(
          Object.assign({}, globalStat, {
            isLink: true,
            linkAttrs: {
              role: 'button',
              onClick: () => onRequestMissingInfo(getIncompleteAddressGuestGroupIds(guestGroups)),
            },
            tooltip: 'We\'ll help you collect all of these. Click on "Missing Addresses" to begin.',
          })
        );
      } else if (globalStat.key === 'guests' && globalStat.value === definitelyInvitedCount) {
        formattedGlobalStats.push(Object.assign({}, globalStat, { hidden: true }));
      } else if (globalStat.key === 'guests' && globalStat.value !== definitelyInvitedCount) {
        formattedGlobalStats.push(
          Object.assign({}, globalStat, { hidden: false, tooltip: 'Total guests added' })
        );
      } else if (globalStat.key === 'invited_guests' && globalStat.value > 0) {
        formattedGlobalStats.push(globalStat);
      } else if (globalStat.key === 'adults' || globalStat.key === 'children') {
        numGuestsAttending += globalStat.value;
        formattedGuestAgeStats.push(globalStat);
      } else {
        formattedGlobalStats.push(globalStat);
      }
    });

    const showHotelPlannerLink = numGuests > 11 && useHotelPlanner;

    return (
      <div>
        <SecondaryNavV2 activeLinkId={NAV_DATA.SECONDARY.GUEST_LIST.ALL_GUESTS} />
        {numGuests === 0 ? (
          <div>
            {wedding.owner_first_name && (
              <React.Fragment>
                {isGuestListActivationV2 ? <NoGuestsOnboardFormV2 /> : <NoGuestsOnboardForm />}
              </React.Fragment>
            )}
          </div>
        ) : (
          <div className="guest__all">
            {showHotelPlannerLink && (
              <Banner type="marketing">
                Need help finding hotels for your guests?
                <Link
                  arrow
                  className="travel-affiliate-link"
                  role="link"
                  target="_blank"
                  href="http://zola.hotelplanner.com/?kw=guestlist"
                  rel="noopener noreferrer"
                  variant="tertiary"
                >
                  Book Hotel Blocks
                </Link>
              </Banner>
            )}
            <div className={`content-top ${isFlagGuestListMobileFix ? 'guests-list__header' : ''}`}>
              {showTour && (
                <React.Fragment>
                  <MediaQuery maxWidth={767}>
                    <Joyride
                      steps={stepsMobile}
                      callback={this.handleJoyrideCallback}
                      showProgress
                      disableScrolling
                      continuous
                      showSkipButton
                      hideBackButton
                      styles={{ options: { zIndex: '1000' } }}
                      tooltipComponent={TourTooltip}
                      floaterProps={{
                        hideArrow: true,
                        disableAnimation: true,
                        styles: {
                          floaterCentered: {
                            width: '100%',
                            transform: 'translate(-50%, 0)',
                            bottom: 0,
                            top: 'initial',
                          },
                        },
                      }}
                    />
                  </MediaQuery>
                  <MediaQuery minWidth={768}>
                    <Joyride
                      steps={steps}
                      callback={this.handleJoyrideCallback}
                      showProgress
                      continuous
                      showSkipButton
                      hideBackButton
                      scrollOffset={150}
                      styles={{ options: { zIndex: 1000 } }}
                      tooltipComponent={TourTooltip}
                      className="hidden-xs hidden-sm"
                      floaterProps={{ disableAnimation: true }}
                    />
                  </MediaQuery>
                </React.Fragment>
              )}
              <DirectoryNavV2
                formattedGlobalStats={formattedGlobalStats}
                formattedGuestAgeStats={formattedGuestAgeStats}
                onAddGuestGroup={onAddGuestGroup}
                onUploadSpreadsheet={() =>
                  onUploadSpreadsheet(this.state.flowRunId, this.updateFlowRunId)
                }
                numGuestsAttending={numGuestsAttending}
                onConfirmAction={onConfirmAction}
                flowRunId={this.state.flowRunId}
              />
            </div>
            <div
              className={`main-content table guest-table ${
                isFlagGuestListMobileFix ? 'guests-list__main' : ''
              }`}
            >
              <div className="row">
                <div className="col-xs-12">
                  <div className="col-xs-12 navbar-default">
                    <div className="nav navbar-nav navbar-right">
                      <ul className="list-inline">
                        <li
                          id="GL-step5"
                          className="with-tooltip no-tooltip-on-mobile"
                          data-tooltip="Exports guest information to a spreadsheet"
                        >
                          <Dropdown
                            ref={c => {
                              this.exportDropdown = c;
                            }}
                          >
                            <DropdownTrigger>
                              <IconButton
                                btnStyle="secondary"
                                iconClassName="zola-ui-icon-overflow-dots"
                              />
                            </DropdownTrigger>
                            <DropdownContent className="">
                              <ul className="dropdown-menu dropdown-menu-right">
                                <li>
                                  <a
                                    href="/web-api/v1/guestgroup/export/guest-excel"
                                    onClick={this.afterExport}
                                  >
                                    Export Excel Format
                                  </a>
                                </li>
                                <li>
                                  <a
                                    href="/web-api/v1/guestgroup/export/all"
                                    onClick={this.afterExport}
                                  >
                                    Export .csv Format
                                  </a>
                                </li>
                                <li>
                                  <a href="/weddings-files/zola_guest_list_template.csv">
                                    Download Spreadsheet Template
                                  </a>
                                </li>
                              </ul>
                            </DropdownContent>
                          </Dropdown>
                        </li>
                      </ul>
                    </div>
                    <div className="nav navbar-nav navbar-left">
                      {checkedRows.length > 0 ? (
                        <ul className="list-inline bulk-actions">
                          <li>
                            <Dropdown
                              ref={c => {
                                this.bulkActionsDropdown = c;
                              }}
                            >
                              <DropdownTrigger className="btn-dropdown btn-dropdown-secondary navbar-btn btn btn-boxed">
                                <div
                                  className="with-tooltip no-tooltip-on-mobile"
                                  data-tooltip="Perform actions on multiple guests at once."
                                >
                                  <span>Bulk Actions</span>
                                  <i className="zolaicon zolaicon-chevron-down" />
                                </div>
                              </DropdownTrigger>
                              <DropdownContent>
                                <ul className="dropdown-menu dropdown-menu-dark dropdown-menu-left">
                                  <li>
                                    <a
                                      role="button"
                                      onClick={() => {
                                        onRequestMissingInfo(checkedRows);
                                        this.bulkActionsDropdown.hide();
                                      }}
                                    >
                                      Request Missing Info
                                    </a>
                                  </li>
                                  <li>
                                    <a
                                      role="button"
                                      onClick={() => {
                                        onGuestMessage(checkedRows);
                                        this.bulkActionsDropdown.hide();
                                      }}
                                    >
                                      Send Message
                                    </a>
                                  </li>
                                  <li>
                                    <a
                                      role="button"
                                      onClick={() => {
                                        onToggleInvited(checkedRows, true);
                                        this.bulkActionsDropdown.hide();
                                      }}
                                    >
                                      Set to &ldquo;Definitely Invited&rdquo;
                                    </a>
                                  </li>
                                  <li>
                                    <a
                                      role="button"
                                      onClick={() => {
                                        onToggleInvited(checkedRows, false, true);
                                        this.bulkActionsDropdown.hide();
                                      }}
                                    >
                                      Set to &ldquo;Maybe Invited&rdquo;
                                    </a>
                                  </li>
                                  <li>
                                    <a
                                      role="button"
                                      onClick={() => {
                                        onDeleteGuestGroups(checkedRows);
                                        this.bulkActionsDropdown.hide();
                                      }}
                                    >
                                      Delete
                                    </a>
                                  </li>
                                </ul>
                              </DropdownContent>
                            </Dropdown>
                          </li>
                          <li>
                            {checkedRows.length} Selected.{' '}
                            <a role="button" onClick={this.unCheckAllRows}>
                              Clear Selection
                            </a>
                          </li>
                        </ul>
                      ) : (
                        <ul className="list-inline filter-search-ul-v2">
                          <li>
                            <Dropdown>
                              <DropdownTrigger className="btn-dropdown navbar-btn btn btn-boxed">
                                {numSelectedFacets ? (
                                  <span>{numSelectedFacets} Filters Selected</span>
                                ) : (
                                  <div
                                    className="with-tooltip no-tooltip-on-mobile"
                                    data-tooltip="View specific groups of guests and perform bulk actions."
                                  >
                                    <span>Filter Guests ({numGuests})</span>
                                  </div>
                                )}
                                <i className="zolaicon zolaicon-chevron-down" />
                              </DropdownTrigger>
                              <DropdownContent>
                                <div className="dropdown-menu dropdown-menu-left">
                                  <Facets
                                    selectedFacetValues={selectedFacetValues}
                                    onToggleFacet={onToggleFacet}
                                    onResetFacets={this.resetFacets}
                                  />
                                </div>
                              </DropdownContent>
                            </Dropdown>
                          </li>
                          <li>
                            <div className="has-feedback has-feedback-left">
                              <span className="zolaicon clear-search hidden" />
                              <i className="glyphicon glyphicon-search form-control-feedback hidden-xs hidden-sm" />
                              <input
                                className="form-control search"
                                type="search"
                                placeholder="Search Guest List"
                                onChange={e => onSearchFilterGuestList(e)}
                                value={searchValue}
                              />
                            </div>
                          </li>
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                {filteredIds.length === 0 ? (
                  <div className="col-xs-12 text-center">
                    <h3>No Results Found</h3>
                    <p>Try adjusting your search or filter to find what you&apos;re looking for.</p>
                    <ButtonV2 className="margin-top-2x" onClick={this.showAllGuests}>
                      Show All Guests
                    </ButtonV2>
                  </div>
                ) : (
                  <div className="col-xs-12">
                    {filteredIds.length > 0 && (
                      <StickyHeaderTable
                        StaticTable={GuestsTable}
                        StickyHeader={GuestsHeader}
                        dataPropName="guestGroups"
                        stickyHeaderCls={`table guest-table-v2 ${
                          isFlagGuestListMobileFix ? 'guests-list__table' : ''
                        }`}
                        checkAllRows={this.checkAllRows}
                        checkedAllRows={checkedAllRows}
                        onToggleSort={onToggleSort}
                        sortByNameAsc={sortByNameAsc}
                        filteredIds={filteredIds}
                        guestGroups={guestGroups}
                        editGuestModalOpen={editGuestModalOpen}
                        selectedGuestGroupId={selectedGuestGroupId}
                        onToggleInvited={onToggleInvited}
                        onEditGuestGroup={onEditGuestGroup}
                        onDeleteGuestGroups={onDeleteGuestGroups}
                        wedding={wedding}
                        onGuestMessage={onGuestMessage}
                        checkedRows={checkedRows}
                        checkRow={this.checkRow}
                        onSelectGuestGroup={onSelectGuestGroup}
                        onRequestMissingInfo={onRequestMissingInfo}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {numGuests <= 2 && <NoGuestValueProps />}
      </div>
    );
  }
}

GuestDirectory.propTypes = {
  getGuestList: PropTypes.func.isRequired,
  isMapping: PropTypes.bool,
  onAddGuestGroup: PropTypes.func.isRequired,
  onUploadSpreadsheet: PropTypes.func.isRequired,
  onToggleInvited: PropTypes.func.isRequired,
  onEditGuestGroup: PropTypes.func.isRequired,
  guestGroups: PropTypes.shape({
    uuid: PropTypes.string,
    guests: PropTypes.arrayOf(
      PropTypes.shape({
        family_name: PropTypes.string,
        first_name: PropTypes.string,
      })
    ),
  }),
  globalStats: PropTypes.arrayOf(PropTypes.shape({})),
  numGuests: PropTypes.number,
  numSelectedFacets: PropTypes.number,
  onDeleteGuestGroups: PropTypes.func.isRequired,
  wedding: PropTypes.shape({
    owner_first_name: PropTypes.string,
    partner_first_name: PropTypes.string,
  }),
  onToggleFacet: PropTypes.func.isRequired,
  onGuestMessage: PropTypes.func.isRequired,
  sortByNameAsc: PropTypes.bool,
  onToggleSort: PropTypes.func.isRequired,
  onResetFacets: PropTypes.func.isRequired,
  selectedFacetValues: PropTypes.shape({}),
  onUnload: PropTypes.func.isRequired,
  editGuestModalOpen: PropTypes.bool,
  initialized: PropTypes.bool,
  onConfirmAction: PropTypes.func,
  onRequestMissingInfo: PropTypes.func,
  getEvents: PropTypes.func,
  onSearchFilterGuestList: PropTypes.func,
  searchValue: PropTypes.string,
  filteredIds: PropTypes.arrayOf(PropTypes.number),
  selectedGuestGroupId: PropTypes.number,
  onResetSearchValue: PropTypes.func,
  onSelectGuestGroup: PropTypes.func,
  showTour: PropTypes.bool.isRequired,
  onDisableTour: PropTypes.func,
  weddingAcctUUID: PropTypes.string,
};

const mapStateToProps = state => ({
  wedding: state.wedding.wedding,
  isMapping: state.guestList.guestImport.isMapping,
  showTour: !state.wedding.account.disable_guest_manager_tour,
  weddingAcctUUID: state.wedding.account.uuid,
});

const mapDispatchToProps = dispatch => ({
  getGuestList: () => dispatch(fetchGuestList()),
  onAddGuestGroup: guestGroupType => {
    dispatch(resetMapping());
    dispatch(
      showModal('ADD_GUEST_GROUP', {
        callback: () => dispatch(fetchGuestList()),
        guestGroupType,
        formIdentifier: 'addGuestGroupForm',
        sendLinkVisible: false,
      })
    );
  },
  onUploadSpreadsheet: (flowRunId, updateFlowRunId) => {
    dispatch(
      showModal(
        'ADD_GUEST_GROUP',
        {
          callback: () => dispatch(resetMapping()), // reset data on open… TODO: make a callback available to modals when closing
          formIdentifier: 'addGuestGroupForm',
          activeTab: 'UPLOAD_SPREADSHEET',
          sendLinkVisible: false,
          flowRunId,
          updateFlowRunId,
        },
        { size: 'md' }
      )
    );
    dispatch(resetMapping());
  },
  onEditGuestGroup: (guestGroupId, activeTab) => {
    const callback = () => dispatch(fetchGuestList());
    return dispatch(getGuestGroup(guestGroupId)).then(() =>
      dispatch(
        showModal('EDIT_GUEST_GROUP_WEDDING', {
          guestGroupId,
          activeTab,
          callback,
          formIdentifier: 'editGuestGroupForm',
        })
      )
    );
  },
  onToggleInvited: (guestGroupIds, isInvited, hasResponses) => {
    if (isInvited) {
      dispatch(toggleInvited(guestGroupIds, isInvited, fetchGuestList));
    } else if (hasResponses) {
      const onConfirmAction = () =>
        dispatch(toggleInvited(guestGroupIds, isInvited, fetchGuestList)).then(() =>
          dispatch(hideModal())
        );
      dispatch(showModal('UNINVITE_GUEST_CONFIRMATION', { onConfirmAction }, { size: 'md' }));
    } else {
      dispatch(toggleInvited(guestGroupIds, isInvited, fetchGuestList));
    }
  },
  onDeleteGuestGroups: guestGroupIds =>
    dispatch(showModal('DELETE_GUEST_GROUP', { guestGroupIds })),
  onToggleFacet: (facetKey, facetValue) =>
    dispatch(toggleAllGuestsFacetValue(facetKey, facetValue)),
  onToggleSort: () => {
    dispatch(toggleSortByNameAsc());
    dispatch(fetchGuestList());
  },
  onDisableTour: uuid =>
    dispatch(updateFeatureFlag(uuid, [{ name: 'disable_guest_manager_tour_at', value: true }])),
});

export default connect(mapStateToProps, mapDispatchToProps)(GuestDirectory);



// WEBPACK FOOTER //
// ./src/components/manage/guestlist/Directory.jsx